// Copyright (c) 2020 WiseTime. All rights reserved.

import React from "react"
import styled from "styled-components/macro"
import { TrashStatus, UnTrashTimeRowsRequest } from "generated/wisetime/user/timeline/v4/timeline_pb"
import SvgIcon from "features/shared/SvgIcon"
import * as TimelineActions from "../actions"
import { useDispatch } from "react-redux"
import { Loader } from "../../shared/styles"
import { t } from "@lingui/macro"
import { darken } from "polished"

const Wrapper = styled.span`
  position: absolute;
  right: 0;
  top: 7px;
  z-index: 1;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }): string => theme.colors.white};
  border-radius: 50%;
  transform: translateX(1rem);
  transition: all .3s ease;
`

export const WrapperUntrash = styled(Wrapper)<{ isActive: boolean }>`
  color: ${({ theme }): string => theme.colors.indigo};
  top: 5px;
  width: 2.4rem;
  height: 2.4rem;
  background-color: transparent;
  border-radius: 0;

  &:hover {
    color: ${({ theme }): string => darken(0.1, theme.colors.indigo)};
  }

  @media (min-width: ${({ theme }): string => theme.screen.md}) {
    display: none;
    top: 0;
    transform: translateX(100%);
    width: 3.4rem;
    height: 36px;
  }

  @media (max-width: ${({ theme }): string => theme.screen.md}) {
    display: none;
  }
`

export const WrapperUntrashPending = styled(WrapperUntrash)`
  cursor: default;
  display: flex;
`

export const StyledTrashRestoreIcon = styled(SvgIcon)`
  width: 1.2rem;
  min-width: 1.2rem;
  height: 1.2rem;
`

type Props = {
  timeRowId: string,
  trashStatus?: TrashStatus,
  pendingStatus?: TrashStatus,
  errorMessage?: string,
  isActive?: boolean,
}

export const RowState = ({
  timeRowId,
  trashStatus,
  pendingStatus,
  isActive = false,
}: Props): React.ReactElement<Props> | null => {
  const dispatch = useDispatch()
  const untrashRow = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation()
    const request = new UnTrashTimeRowsRequest().addTimeRowIds(timeRowId)
    dispatch(TimelineActions.untrashTimeRows(request))
  }
  if (trashStatus === TrashStatus.TRASHED) {
    const untrashing = pendingStatus === TrashStatus.NOT_TRASHED

    if (untrashing) {
      return (
        <WrapperUntrashPending
          isActive={isActive}
          onClick={event => event.stopPropagation()}
        >
          <Loader/>
        </WrapperUntrashPending>
      )
    }

    return (
      <WrapperUntrash
        as="button"
        type="button"
        title={t({ id: "button_restore", message: "Restore" })}
        onClick={untrashRow}
        isActive={isActive}
      >
        <StyledTrashRestoreIcon name="trash-restore"/>
      </WrapperUntrash>
    )
  }
  return null
}

export default RowState
